/**
 * 标准配合圆形距离条件工具。
 *
 * 文件职责：
 * 1. 表达 SOLIDWORKS Distance Mate 的第一/第二圆形实体条件；
 * 2. 将中心、最小、最大条件转换为半径偏移；
 * 3. 为测量、求解和控制器默认值提供同一套纯函数。
 */

import {
  MateDistanceMode,
  type MateDistanceEntityModes,
  type MateEntityRef,
} from '../../definition';
import { resolveAxisGeometry } from '../geometry/StandardMateGeometryAccess';

/** 圆形距离条件计算容差。 */
const STANDARD_MATE_ROUND_DISTANCE_EPSILON = 1e-9;

/**
 * 读取实体的圆形距离半径。
 *
 * @param entity 配合实体。
 * @returns 圆柱、圆边等实体的有效半径；非圆形实体返回 0。
 */
export function resolveStandardMateRoundDistanceRadius(entity: MateEntityRef): number {
  const axis = resolveAxisGeometry(entity);
  if (typeof axis?.radius === 'number' && Number.isFinite(axis.radius) && axis.radius > 0) {
    return axis.radius;
  }

  return 0;
}

/**
 * 判断实体是否有 SOLIDWORKS 圆形距离条件。
 *
 * @param entity 配合实体。
 * @returns 是否可显示中心/最小/最大条件。
 */
export function hasStandardMateRoundDistanceEntity(entity: MateEntityRef): boolean {
  return resolveStandardMateRoundDistanceRadius(entity) > STANDARD_MATE_ROUND_DISTANCE_EPSILON;
}

/**
 * 解析单个实体的有效圆形距离条件。
 *
 * @param entity 配合实体。
 * @param explicitMode 明确保存的逐实体条件。
 * @param fallbackMode 旧文档或旧 UI 保存的全局距离条件。
 * @returns 可用于计算的实体条件。
 */
function resolveEntityDistanceMode(
  entity: MateEntityRef,
  explicitMode: MateDistanceMode | undefined,
  fallbackMode: MateDistanceMode,
): MateDistanceMode {
  if (!hasStandardMateRoundDistanceEntity(entity)) {
    return MateDistanceMode.CenterToCenter;
  }
  return explicitMode ?? fallbackMode;
}

/**
 * 解析当前 Mate Definition 应使用的逐实体圆形距离条件。
 *
 * @param first 第一选择实体。
 * @param second 第二选择实体。
 * @param fallbackMode 旧全局距离条件。
 * @param distanceEntityModes 新逐实体距离条件。
 * @returns 两个实体的有效距离条件。
 */
export function resolveStandardMateDistanceEntityModes(
  first: MateEntityRef,
  second: MateEntityRef,
  fallbackMode: MateDistanceMode = MateDistanceMode.Minimum,
  distanceEntityModes?: MateDistanceEntityModes,
): MateDistanceEntityModes {
  return {
    firstEntityMode: resolveEntityDistanceMode(
      first,
      distanceEntityModes?.firstEntityMode,
      fallbackMode,
    ),
    secondEntityMode: resolveEntityDistanceMode(
      second,
      distanceEntityModes?.secondEntityMode,
      fallbackMode,
    ),
  };
}

/**
 * 解析 SOLIDWORKS 圆形距离条件的默认逐实体值。
 *
 * @param first 第一选择实体。
 * @param second 第二选择实体。
 * @returns 默认逐实体距离条件。
 */
export function resolveDefaultStandardMateDistanceEntityModes(
  first: MateEntityRef,
  second: MateEntityRef,
): MateDistanceEntityModes {
  const fallbackMode = hasStandardMateRoundDistanceEntity(first) || hasStandardMateRoundDistanceEntity(second)
    ? MateDistanceMode.CenterToCenter
    : MateDistanceMode.Minimum;
  return resolveStandardMateDistanceEntityModes(first, second, fallbackMode);
}

/**
 * 将单个实体条件转换为中心距半径偏移。
 *
 * 设计说明：
 * - Minimum 表示测量到靠近另一实体的一侧，所以中心距需要加上该实体半径；
 * - Maximum 表示测量到远离另一实体的一侧，所以中心距需要减去该实体半径；
 * - CenterToCenter 不引入半径偏移。
 *
 * @param entity 配合实体。
 * @param mode 实体距离条件。
 * @returns 从尺寸值换算到中心距时需要增加的偏移。
 */
function calculateEntityRadiusOffset(entity: MateEntityRef, mode: MateDistanceMode): number {
  const radius = resolveStandardMateRoundDistanceRadius(entity);
  if (radius <= STANDARD_MATE_ROUND_DISTANCE_EPSILON) {
    return 0;
  }
  if (mode === MateDistanceMode.Minimum) {
    return radius;
  }
  if (mode === MateDistanceMode.Maximum) {
    return -radius;
  }
  return 0;
}

/**
 * 计算从用户输入尺寸到中心距的半径偏移。
 *
 * @param first 第一选择实体。
 * @param second 第二选择实体。
 * @param modes 逐实体距离条件。
 * @returns 需要加到尺寸绝对值上的半径偏移。
 */
export function calculateStandardMateDistanceRadiusOffset(
  first: MateEntityRef,
  second: MateEntityRef,
  modes: MateDistanceEntityModes,
): number {
  return calculateEntityRadiusOffset(first, modes.firstEntityMode)
    + calculateEntityRadiusOffset(second, modes.secondEntityMode);
}

/**
 * 按逐实体距离条件把中心距换算为实际显示/约束尺寸。
 *
 * @param centerDistance 两个实体中心线或中心点之间的距离。
 * @param first 第一选择实体。
 * @param second 第二选择实体。
 * @param modes 逐实体距离条件。
 * @returns SOLIDWORKS Distance Mate 中显示或约束的尺寸；不可用时返回 null。
 */
export function calculateStandardMateDistanceValueFromCenterDistance(
  centerDistance: number,
  first: MateEntityRef,
  second: MateEntityRef,
  modes: MateDistanceEntityModes,
): number | null {
  if (!Number.isFinite(centerDistance)) {
    return null;
  }

  const value = centerDistance - calculateStandardMateDistanceRadiusOffset(first, second, modes);
  return value >= -STANDARD_MATE_ROUND_DISTANCE_EPSILON
    ? Math.max(0, value)
    : null;
}

/**
 * 判断指定实体是否能使用某个圆形距离条件。
 *
 * @param entity 配合实体。
 * @param centerDistance 当前中心距。
 * @param mode 待判断距离条件。
 * @returns 是否可用。
 */
export function canUseStandardMateRoundDistanceMode(
  entity: MateEntityRef,
  centerDistance: number,
  mode: MateDistanceMode,
): boolean {
  if (!Number.isFinite(centerDistance)) {
    return false;
  }
  if (mode !== MateDistanceMode.Maximum) {
    return mode === MateDistanceMode.CenterToCenter || hasStandardMateRoundDistanceEntity(entity);
  }

  /**
   * Maximum Distance 对应圆形实体远离另一实体的一侧。SOLIDWORKS 中该侧距离始终可定义，
   * 因此不再用“中心距必须大于半径”这种项目内假设禁用选项。
   */
  return hasStandardMateRoundDistanceEntity(entity);
}

/**
 * 解析单个圆形实体可用的距离条件。
 *
 * @param entity 当前实体。
 * @param centerDistance 当前两个实体的中心距。
 * @returns 可用于 PropertyManager 单选组的条件列表。
 */
export function resolveStandardMateRoundDistanceModeOptions(
  entity: MateEntityRef,
  centerDistance: number | null,
): readonly {
  /** 距离条件。 */
  readonly mode: MateDistanceMode;
  /** 是否可用。 */
  readonly enabled: boolean;
  /** 禁用原因。 */
  readonly disabledReason?: string;
}[] {
  if (!hasStandardMateRoundDistanceEntity(entity) || centerDistance === null) {
    return [];
  }

  const maximumEnabled = canUseStandardMateRoundDistanceMode(entity, centerDistance, MateDistanceMode.Maximum);
  return [
    {
      mode: MateDistanceMode.CenterToCenter,
      enabled: true,
    },
    {
      mode: MateDistanceMode.Minimum,
      enabled: true,
    },
    {
      mode: MateDistanceMode.Maximum,
      enabled: maximumEnabled,
      disabledReason: maximumEnabled
        ? undefined
        : '当前实体不是可测量圆形距离条件的实体。',
    },
  ];
}
